// TEMPORARY: Remove once React SPA migration is complete.
import type {UseMutationOptions, UseQueryOptions} from '@tanstack/react-query';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {buildURL, fetcher, RequestMethod} from '#utils/http.js';

const REACT_PAGES_ENDPOINT = '/api/admin/react_pages';
const REACT_PAGES_V3_ENDPOINT = '/api/v3/react_pages';

export type PageConfig = {
  slug: string;
  label: string;
  path: string;
  enabled: boolean;
};

export type AdminPageConfig = PageConfig & {
  organization_override: boolean | null;
  global_override: boolean | null;
  organization_ids: number[];
};

export type ReactPagesScope = number | 'global';

type ReactPagesApiResponse = {
  pages: AdminPageConfig[];
};

type ReactPagesV3ApiResponse = {
  data: PageConfig[];
};

type ReactPagesResponse = PageConfig[];

type UpdateReactPagesRequest = {
  scope: ReactPagesScope;
  pages: Record<string, boolean | null>;
  organizations?: Record<string, Record<string, boolean>>;
};

type AdminReactPagesQueryOptions = Omit<
  UseQueryOptions<AdminPageConfig[]>,
  'queryKey' | 'queryFn'
>;

type ReactPagesQueryOptions = Omit<
  UseQueryOptions<ReactPagesResponse>,
  'queryKey' | 'queryFn'
>;

type UpdateReactPagesMutationOptions = Omit<
  UseMutationOptions<unknown, Error, UpdateReactPagesRequest>,
  'mutationFn'
>;

export const reactPagesQueryKeys = {
  all: ['react_pages'] as const,
  global: () => [...reactPagesQueryKeys.all, 'global'] as const,
  currentOrg: () => [...reactPagesQueryKeys.all, 'current'] as const,
  byOrg: (orgId: number) => [...reactPagesQueryKeys.all, 'org', orgId] as const,
} as const;

export async function fetchReactPages(
  scope: ReactPagesScope,
  signal?: AbortSignal
): Promise<AdminPageConfig[]> {
  const response = await fetcher<ReactPagesApiResponse>(
    `${REACT_PAGES_ENDPOINT}?${scope === 'global' ? 'scope=global' : `organization_id=${scope}`}`,
    {method: RequestMethod.GET, signal}
  );

  return response.pages;
}

export function reactPagesQueryOptions(
  scope: ReactPagesScope,
  options?: AdminReactPagesQueryOptions
): UseQueryOptions<AdminPageConfig[]> {
  return {
    queryKey:
      scope === 'global'
        ? reactPagesQueryKeys.global()
        : reactPagesQueryKeys.byOrg(scope),
    queryFn: ({signal}) => fetchReactPages(scope, signal),
    ...options,
  };
}

export function useReactPages(
  scope: ReactPagesScope | null,
  options?: AdminReactPagesQueryOptions
) {
  return useQuery(
    reactPagesQueryOptions(scope ?? 'global', {
      enabled: scope !== null,
      ...options,
    })
  );
}

async function updateReactPages(
  request: UpdateReactPagesRequest
): Promise<unknown> {
  return fetcher(REACT_PAGES_ENDPOINT, {
    method: RequestMethod.PUT,
    body: {
      ...(request.scope === 'global'
        ? {scope: 'global'}
        : {organization_id: request.scope}),
      pages: request.pages,
      ...(request.organizations && {organizations: request.organizations}),
    },
  });
}

export function useUpdateReactPages(options?: UpdateReactPagesMutationOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateReactPages,
    onSuccess: () =>
      queryClient.invalidateQueries({queryKey: reactPagesQueryKeys.all}),
    ...options,
  });
}

// --- Current org (non-admin) ---

export async function fetchCurrentOrgReactPages(
  signal?: AbortSignal
): Promise<ReactPagesResponse> {
  const url = buildURL(REACT_PAGES_V3_ENDPOINT, {
    'fields[react_pages]': ['slug', 'label', 'path', 'enabled'],
  });
  const response = await fetcher<ReactPagesV3ApiResponse>(url.toString(), {
    method: RequestMethod.GET,
    signal,
  });

  return response.data;
}

export function currentOrgReactPagesQueryOptions(
  options?: ReactPagesQueryOptions
): UseQueryOptions<ReactPagesResponse> {
  return {
    queryKey: reactPagesQueryKeys.currentOrg(),
    queryFn: ({signal}) => fetchCurrentOrgReactPages(signal),
    staleTime: 30_000,
    ...options,
  };
}

export function useCurrentOrgReactPages(options?: ReactPagesQueryOptions) {
  return useQuery(
    currentOrgReactPagesQueryOptions({
      refetchInterval: 30_000,
      ...options,
    })
  );
}
